import { moodMap } from '../ClassroomDashboard/PeekavilleDataHelpers';
import type { JournalData } from '@peekapak/types/ReportDataExchangeTypes';

export interface SnapshotCheckIn {
  userId: string;
  mood: string;
  createdAt: number;
  sources: string[];
}

export interface WeeklySnapshot {
  checkedIn: number;
  rosterSize: number;
  totalCheckIns: number;
  topMoods: string[];
  topSources: string[];
}

export function parseSources(reasons: string | undefined): string[] {
  if (!reasons) return [];
  return reasons
    .split(',')
    .map((reason) => reason.trim())
    .filter(Boolean);
}

function topThree(values: string[]): string[] {
  const counts = new Map<string, number>();
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
  return [...counts.entries()]
    .sort(([a, countA], [b, countB]) => countB - countA || a.localeCompare(b))
    .slice(0, 3)
    .map(([value]) => value);
}

// weekEnd is exclusive
export function getWeeklySnapshot({
  checkIns,
  rosterUserIds,
  weekStart,
  weekEnd,
}: {
  checkIns: SnapshotCheckIn[];
  rosterUserIds: string[];
  weekStart: number;
  weekEnd: number;
}): WeeklySnapshot {
  const inWeek = checkIns.filter((c) => c.createdAt >= weekStart && c.createdAt < weekEnd);
  const roster = new Set(rosterUserIds);

  return {
    checkedIn: new Set(inWeek.map((c) => c.userId).filter((userId) => roster.has(userId))).size,
    rosterSize: roster.size,
    totalCheckIns: inWeek.length,
    topMoods: topThree(inWeek.map((c) => c.mood)),
    topSources: topThree(inWeek.flatMap((c) => c.sources)),
  };
}

export function getCalendarWeekBounds(weekStart: number): [number, number] {
  const start = new Date(weekStart);
  return [
    new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime(),
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7).getTime(),
  ];
}

export function getMondayWeekBounds(date: Date): [number, number] {
  const daysSinceMonday = (date.getDay() + 6) % 7;
  return getCalendarWeekBounds(
    new Date(date.getFullYear(), date.getMonth(), date.getDate() - daysSinceMonday).getTime(),
  );
}

interface ElementaryJournalOutput {
  userIdHash: string;
  firstName?: string;
  lastName?: string;
  journals: Record<string, { createdAt: number; feeling?: string | number }>;
}

interface RosterStudent {
  userId: string;
  firstName?: string;
  lastName?: string;
}

const normalizedName = (firstName = '', lastName = '') => `${firstName} ${lastName}`.replace(/\s+/g, '').toLowerCase();

export function toElementaryCheckIns(output: ElementaryJournalOutput[], roster: RosterStudent[]): SnapshotCheckIn[] {
  const resolveUserId = (student: ElementaryJournalOutput) => {
    if (roster.some((r) => r.userId === student.userIdHash)) return student.userIdHash;
    const byName = roster.find(
      (r) =>
        student.firstName &&
        normalizedName(r.firstName, r.lastName) === normalizedName(student.firstName, student.lastName),
    );
    return byName?.userId ?? student.userIdHash;
  };

  return output.flatMap((student) => {
    const userId = resolveUserId(student);
    return Object.values(student.journals)
      .map((journal) => ({
        userId,
        mood: typeof journal.feeling === 'string' ? journal.feeling : moodMap[journal.feeling ?? 0],
        createdAt: journal.createdAt,
        sources: [],
      }))
      .filter((checkIn) => checkIn.mood && checkIn.mood !== 'No check-in');
  });
}

// weekEnd is exclusive
export function formatWeekLabel(weekStart: number, weekEnd: number): string {
  const lastDay = new Date(weekEnd - 1);
  const monthDay = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${monthDay(new Date(weekStart))} to ${monthDay(lastDay)}, ${lastDay.getFullYear()}`;
}

export function toSecondaryCheckIns(classData: JournalData[]): SnapshotCheckIn[] {
  return classData.flatMap((student) =>
    student.Progress.filter((entry) => entry.feeling).map((entry) => ({
      userId: entry.userId,
      mood: entry.feeling,
      createdAt: entry.createdAt,
      sources: parseSources(entry.reasons),
    })),
  );
}
